import type { UserRole } from "@myapp/contract";
import { PATHS } from "../../../shared/config/paths";
import type { ISelectOption } from "../../../shared/types/ISelectOption";

/**
 * What a push notification can open, in the words the people who send them use.
 *
 * The field this replaces was free text: any string starting with `/` passed, including paths
 * no route answers to, and nothing said which roles could open the result. This catalog adds
 * the three things a dropdown needs on top of a path — a German label, who may open it, and
 * what still has to be filled in — while the value that crosses the wire stays exactly what it
 * was, a plain app-relative URL.
 *
 * TWO THINGS THAT LOOK LIKE MISTAKES AND ARE NOT:
 *
 * 1. **A target is a URL, not a route.** The Aufgaben and Termin edit screens are modals whose
 *    state lives in the query string (`features/task-upsert`, `features/appointment-upsert`),
 *    so their templates carry `?…` and are deep-linkable exactly like a path. `token`
 *    substitution does not care which half of the URL it lands in.
 *
 * 2. **Some params never reach the URL.** `token: null` marks a value collected only to look
 *    something up — the Auftragsnummer in front of the Handwerker-Termin exists because the API
 *    has no appointment list, not because `/worker/appointments/:appointmentId` wants it.
 *
 * `roles` is copied from `routeConfig.tsx`, deliberately not from `navConfig.ts`: the two
 * disagree in places, and what matters here is who `RequireRole` lets through, not who sees a
 * link in the sidebar.
 */

export const PUSH_TARGET_NONE = "none";
export const PUSH_TARGET_CUSTOM = "custom";

/** Which control collects the value, and therefore what the label above it means. */
export type PushTargetParamKind = "work-order" | "task" | "appointment";

export interface PushTargetParam {
  /** Form field holding the value. Two slots is the most any target needs. */
  field: "param1" | "param2";
  kind: PushTargetParamKind;
  label: string;
  /** Placeholder in the template. `null` = lookup aid only, never substituted. */
  token: string | null;
  /** Shown when the field is empty on submit. */
  requiredMessage: string;
}

export interface PushTargetOption {
  id: string;
  label: string;
  group: string;
  /** Path, optionally with a query string. Tokens may appear in either half. */
  path: string;
  /** Undefined = every signed-in user can open it. */
  roles?: UserRole[];
  params?: PushTargetParam[];
  /** One extra sentence for the preview, where the route has a rule nobody can guess. */
  note?: string;
  /**
   * How the finished target is named in prose — in the preview under the field and in the
   * Verlauf, which is why it takes the values back rather than reading the form. Without one,
   * the label is used with its trailing ellipsis removed.
   */
  describe?: (values: { param1: string; param2: string }) => string;
}

const GROUP_ALL = "Für alle";
const GROUP_WORKER = "Nur Handwerker";
const GROUP_OFFICE = "Nur Verwaltung";
const GROUP_SPECIFIC = "Etwas Bestimmtes";
const GROUP_OTHER = "Sonstiges";

/** Query keys owned by the two upsert modals — see their `use*Params` hooks. */
const UPSERT_TASK = "upsert-task=edit&upsert-task-id=";
const UPSERT_APPOINTMENT = "upsert-appointment=edit&upsert-appointment-id=";

const workOrderParam = (token: string | null): PushTargetParam => ({
  field: "param1",
  kind: "work-order",
  label: "Auftragsnummer",
  token,
  requiredMessage: "Auftragsnummer fehlt",
});

const appointmentParam: PushTargetParam = {
  field: "param2",
  kind: "appointment",
  label: "Termin",
  token: ":appointmentId",
  requiredMessage: "Termin auswählen",
};

const taskParam: PushTargetParam = {
  field: "param1",
  kind: "task",
  label: "Aufgabe",
  token: ":taskId",
  requiredMessage: "Aufgabe auswählen",
};

/**
 * Order is the list order, and grouping relies on it: `Select` opens a new heading whenever the
 * group changes, so entries of one group must stay adjacent.
 *
 * Left out on purpose:
 *   - `/aufgaben/plan` — still a placeholder page until the planner is rebuilt.
 *   - the `…/new` forms — a notification should not drop anyone into an empty form.
 *   - `/users/:userId/edit`, `/zeitmanagement/:absenceId/edit` — their ids are internal numbers
 *     with no route to them in the UI, so no sender could fill the field.
 */
export const PUSH_TARGETS: PushTargetOption[] = [
  { id: "aufgaben", label: "Aufgaben", group: GROUP_ALL, path: PATHS.aufgaben },
  { id: "zeitmanagement", label: "Zeitmanagement", group: GROUP_ALL, path: PATHS.zeitmanagement },
  { id: "kalender", label: "Kalender", group: GROUP_ALL, path: PATHS.disposition },
  {
    id: "reschedule",
    label: "Umdisponierung/ Folgetermin",
    group: GROUP_ALL,
    path: PATHS.reschedule,
  },

  {
    id: "worker-calendar",
    label: "Mein Kalender",
    group: GROUP_WORKER,
    path: PATHS.workerCalendar,
    roles: ["WORKER", "SUPERUSER"],
  },
  {
    id: "worker-privatzeit",
    label: "Privatzeit",
    group: GROUP_WORKER,
    path: PATHS.workerPrivatzeit,
    roles: ["WORKER", "SUPERUSER"],
  },

  {
    id: "work-orders",
    label: "Reparaturaufträge",
    group: GROUP_OFFICE,
    path: PATHS.workOrderPage,
    roles: ["MANAGER", "DIRECTOR", "SUPERUSER"],
  },
  {
    id: "controlling",
    label: "Controlling",
    group: GROUP_OFFICE,
    path: PATHS.controlling,
    roles: ["MANAGER", "SUPERUSER"],
  },
  {
    id: "arbeitszeit-report",
    label: "Arbeitszeit-Report",
    group: GROUP_OFFICE,
    path: PATHS.arbeitszeitReport,
    roles: ["DIRECTOR", "SUPERUSER"],
  },
  {
    id: "users",
    label: "User-Verwaltung",
    group: GROUP_OFFICE,
    path: PATHS.users,
    roles: ["DIRECTOR", "SUPERUSER"],
  },
  {
    id: "settings",
    label: "Einstellungen",
    group: GROUP_OFFICE,
    path: PATHS.settings,
    roles: ["SUPERUSER"],
  },

  {
    id: "work-order-detail",
    label: "Reparaturauftrag …",
    group: GROUP_SPECIFIC,
    path: PATHS.workOrderDetail,
    params: [workOrderParam(":workOrderId")],
    describe: ({ param1 }) => `Reparaturauftrag ${param1}`,
  },
  {
    id: "task-worker",
    label: "Aufgabe – Handwerker-Ansicht …",
    group: GROUP_SPECIFIC,
    path: PATHS.workerTask,
    roles: ["WORKER", "SUPERUSER"],
    params: [taskParam],
    // `WorkerTaskPage` sends any other technician back to their calendar — invisible from here.
    note: "Nur der zugewiesene Handwerker kann die Aufgabe öffnen.",
    // "#" marks these as record numbers. The pickers label a Termin by its `sequenceNumber`
    // ("Termin 1") and an Aufgabe by its title, so a bare number here would read as the one the
    // sender just saw rather than the id in the address below it.
    describe: ({ param1 }) => `Aufgabe #${param1} in der Handwerker-Ansicht`,
  },
  {
    id: "task-office",
    label: "Aufgabe – Büro-Ansicht …",
    group: GROUP_SPECIFIC,
    path: `${PATHS.aufgaben}?${UPSERT_TASK}:taskId`,
    params: [taskParam],
    note: "Öffnet die Aufgabe direkt im Bearbeiten-Fenster.",
    describe: ({ param1 }) => `Aufgabe #${param1} zum Bearbeiten`,
  },
  {
    id: "appointment-worker",
    label: "Termin – Handwerker-Ansicht …",
    group: GROUP_SPECIFIC,
    path: PATHS.workerAppointment,
    roles: ["WORKER", "SUPERUSER"],
    // The Auftragsnummer is how the Termin is found, not part of the address.
    params: [workOrderParam(null), appointmentParam],
    describe: ({ param2 }) => `Termin #${param2} in der Handwerker-Ansicht`,
  },
  {
    id: "appointment-office",
    label: "Termin – Büro-Ansicht …",
    group: GROUP_SPECIFIC,
    path: `${PATHS.workOrderDetail}?${UPSERT_APPOINTMENT}:appointmentId`,
    params: [workOrderParam(":workOrderId"), appointmentParam],
    note: "Öffnet den Termin direkt im Bearbeiten-Fenster.",
    describe: ({ param1, param2 }) => `Termin #${param2} zu Auftrag ${param1}`,
  },
];

export const findPushTarget = (id: string): PushTargetOption | undefined =>
  PUSH_TARGETS.find((target) => target.id === id);

/**
 * The dropdown's options. The default and the escape hatch bracket the catalog: doing nothing is
 * the first thing offered, and typing a path yourself is the last.
 */
export const pushTargetSelectOptions: ISelectOption[] = [
  { value: PUSH_TARGET_NONE, label: "Nur App öffnen" },
  ...PUSH_TARGETS.map((target) => ({
    value: target.id,
    label: target.label,
    group: target.group,
  })),
  { value: PUSH_TARGET_CUSTOM, label: "Eigener Pfad …", group: GROUP_OTHER },
];
